import { System } from '../../../engine/ecs/core/System';
import { UIComponent } from '../../../engine/ecs/components/UIComponent';
import { PacmanComponent } from '../components/PacmanComponent';
import { GhostComponent } from '../components/GhostComponent';
import { CollectibleComponent } from '../components/CollectibleComponent';

/**
 * UISystem - Handles UI rendering and updates for Pacman game
 */
export class UISystem extends System {
  private context: CanvasRenderingContext2D;

  constructor(context: CanvasRenderingContext2D) {
    super();
    this.registerRequiredComponent(UIComponent);
    this.context = context;
  }

  public update(_deltaTime: number): void {
    // Render UI components
    for (const entity of this.getEntities()) {
      const ui = this.componentManager.getComponent(
        entity,
        UIComponent
      ) as UIComponent;

      if (ui.visible) {
        this.renderText(ui);
      }
    }

    // Render game-specific UI
    this.renderGameUI();
  }

  /**
   * Render game-specific UI elements
   */
  private renderGameUI(): void {
    // Render game stats including super mode status
    this.renderGameStats();
  }

  /**
   * Get current game statistics
   */
  private getGameStats(): {
    dotsRemaining: number;
    ghostCount: number;
    pacmanAlive: boolean;
  } {
    const allEntities = this.entityManager.getAllEntities();
    let dotsRemaining = 0;
    let ghostCount = 0;
    let pacmanAlive = false;

    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, CollectibleComponent)) {
        const collectible = this.componentManager.getComponent(
          entity,
          CollectibleComponent
        ) as CollectibleComponent;
        if (!collectible.isConsumed) {
          dotsRemaining++;
        }
      }

      if (this.componentManager.hasComponent(entity, GhostComponent)) {
        ghostCount++;
      }

      if (this.componentManager.hasComponent(entity, PacmanComponent)) {
        const pacman = this.componentManager.getComponent(
          entity,
          PacmanComponent
        ) as PacmanComponent;
        pacmanAlive = pacman.isAlive;
      }
    }

    return { dotsRemaining, ghostCount, pacmanAlive };
  }

  private renderText(ui: UIComponent): void {
    this.context.save();
    this.context.fillStyle = ui.color;
    this.context.font = `${ui.fontSize}px ${ui.fontFamily}`;

    // Basic text rendering at fixed position for now
    this.context.fillText(ui.text, 10, 50);

    this.context.restore();
  }

  private renderGameStats(): void {
    // Get game statistics
    const stats = this.getGameStats();

    // Check for super mode Pacman
    let isSuperMode = false;
    const allEntities = this.entityManager.getAllEntities();
    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, PacmanComponent)) {
        const pacman = this.componentManager.getComponent(
          entity,
          PacmanComponent
        ) as PacmanComponent;
        if (pacman.isSuperMode) {
          isSuperMode = true;
          break;
        }
      }
    }

    this.context.save();
    this.context.fillStyle = '#FFFFFF';
    this.context.font = '16px Arial';
    this.context.textAlign = 'left';

    // Render basic stats
    this.context.fillText(`Dots: ${stats.dotsRemaining}`, 10, 30);
    this.context.fillText(`Ghosts: ${stats.ghostCount}`, 10, 50);
    this.context.fillText(
      `Pacman Alive: ${stats.pacmanAlive ? 'Yes' : 'No'}`,
      10,
      70
    );

    // Render super mode status
    if (isSuperMode) {
      this.context.fillStyle = '#FFD700';
      this.context.font = 'bold 18px Arial';
      this.context.fillText('🔥🔥 SUPER MODE ! 🔥🔥', 10, 100);
    }

    this.context.restore();
  }
}
